import { GUI } from 'dat.gui';
import { CHEATS_GUI_ENABLED_STORAGE_KEY } from './CheatsToggle';
import { cheats } from '../../Cheats';
import Game from '../../Game';
import { getHighestLevelCompleted } from '../../State/Player/PlayerLevelHelper';
import { BoosterType, PreBoosterType } from '../../State/Card/BoosterType';
import { RewardType } from '../RewardHelper';
import { MobileConsole } from './MobileConsole';
import { Logger } from '../Logger/Logger';
import { LogOrigin } from '../Logger/LogOrigin';
import { PlayerTask } from '../../State/Player/PlayerTasks';
import { TimerType } from '../../System/Timer/TimerTypes';
import { PopupID } from '../../State/UI/PopupID';
import DateHelper from '../DateHelper';
import { LogStyles } from '../Logger/LogStyles';
import { PetId } from '../../Constants/Pets';

export class CheatsGui {
    private readonly gui: GUI;
    private visible: boolean = false;
    private mobileConsole: MobileConsole;

    constructor() {
        this.mobileConsole = new MobileConsole();
        this.gui = new GUI();
        this.gui.hide();

        const self = this;

        const foldersModel = {
            set: [
                {
                    name: 'coins',
                    step: 1,
                    min: 0,
                    set(value: number) {
                        cheats.setCoins(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory.coins;
                    },
                },
                {
                    name: 'level',
                    step: 1,
                    min: 1,
                    set(value: number) {
                        cheats.setLevel(value);
                    },
                    get() {
                        return getHighestLevelCompleted(Game.store.getState().playerState);
                    },
                },
                {
                    name: 'world',
                    step: 1,
                    min: 1,
                    set(value: number) {
                        cheats.setWorld(value);
                    },
                    get() {
                        return Game.store.getState().playerState.maxWorld;
                    },
                },
                {
                    name: 'ads watched',
                    set(value: number) {
                        cheats.setAdsWatched(value);
                    },
                    get() {
                        return Game.store.getState().playerState.progress.adsWatched;
                    },
                },
                {
                    name: 'wheel spins',
                    set(value: number) {
                        cheats.setWheelSpins(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory.wheelSpins;
                    },
                },
                {
                    name: 'initial login days ago',
                    set(value: number) {
                        cheats.setInitialLogin(value);
                    },
                    get() {
                        const date = new Date(Game.store.getState().playerState.timers.get(TimerType.INITIAL_LOGIN));
                        return Math.floor((DateHelper.now.getDay() - date.getTime()) / DateHelper.Day);
                    },
                },
                {
                    name: 'day of week',
                    set(value: number) {
                        cheats.setDay(value);
                    },
                    get() {
                        return Math.floor(DateHelper.now.getDay() / DateHelper.Day);
                    },
                },
                {
                    name: 'player purchases',
                    set(value: number) {
                        cheats.setPlayerPurchases(value);
                    },
                    get() {
                        return Game.store.getState().playerState.purchases;
                    },
                },
                {
                    name: 'pet level',
                    set(value: number) {
                        cheats.setPetLevel(value);
                    },
                    get() {
                        return Game.store.getState().playerState.petLevel;
                    },
                },
                {
                    name: 'difficulty reduction',
                    set(value: number) {
                        cheats.setDifficultyReduction(value);
                    },
                    get() {
                        return Game.store.getState().playerState.progress.difficultyReduction;
                    },
                },
                {
                    name: 'star chest star count',
                    set(value: number) {
                        cheats.setStarChestStarCount(value);
                    },
                    get() {
                        return Game.store.getState().playerState.progress.starChestInitialStars;
                    },
                },
                {
                    name: 'star chest level',
                    set(value: number) {
                        cheats.setStarChestLevel(value);
                    },
                    get() {
                        return Game.store.getState().playerState.starChestLevel || -1;
                    },
                },
                {
                    name: 'server time offset',
                    set(value: number) {
                        cheats.setServerTime(value);
                    },
                    get() {
                        return Game.serverTimeOffset;
                    },
                },
                {
                    name: 'cinematic mode',
                    set(value: boolean) {
                        cheats.cinematicMode(value);
                    },
                    get() {
                        return Game.store.getState().playerState.settings.cinematicMode;
                    },
                },
                {
                    name: 'pause harvest timer',
                    set(value: boolean) {
                        cheats.pauseHarvestTimer(value);
                    },
                    get() {
                        return Game.settings.harvestPaused || false;
                    },
                },
                {
                    name: 'console',
                    set(value: boolean) {
                        self.mobileConsole.visible = value;
                    },
                    get() {
                        return self.mobileConsole.visible;
                    },
                },
            ],
            boosters: [
                {
                    name: 'TNT',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterTNT(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.TNT];
                    },
                },
                {
                    name: 'Bulls Eye',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterBullsEye(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.BULLS_EYE];
                    },
                },
                {
                    name: 'Shuffle',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterShuffle(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.SHUFFLE];
                    },
                },
                {
                    name: 'Undo',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterUndo(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.UNDO];
                    },
                },
                {
                    name: 'Joker',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterJoker(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.JOKER];
                    },
                },
                {
                    name: 'Canister pre-booster',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setPreBoosterCanister(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[PreBoosterType.CANISTER];
                    },
                },
                {
                    name: 'Joker pre-booster',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setPreBoosterJoker(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[PreBoosterType.JOKER];
                    },
                },
                {
                    name: 'Remove 3 pre-booster',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setPreBoosterRemoveThree(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[PreBoosterType.REMOVE_THREE];
                    },
                },
                {
                    name: 'Free play',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setFreePlay(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[RewardType.FREE_PLAY];
                    },
                },
                {
                    name: 'Shop Gift',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setShopGift(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory.shopGift;
                    },
                },
                {
                    name: 'Plus five',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setBoosterPlusFive(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.PLUS_FIVE];
                    },
                },
                {
                    name: 'Extra cards',
                    min: 0,
                    step: 1,
                    set(value: number) {
                        cheats.setExtraCards(value);
                    },
                    get() {
                        return Game.store.getState().playerState.inventory[BoosterType.PLUS_ONE];
                    },
                },
            ],
            log: [
                {
                    name: 'console',
                    set(value: boolean) {
                        self.mobileConsole.visible = value;
                    },
                    get() {
                        return self.mobileConsole.visible;
                    },
                },
                {
                    name: 'fetch FB data',
                    value: async () => {
                        Logger.log('FB data', { origin: LogOrigin.CHEATS, data: [await cheats.fetchFBData()] });
                    },
                },
                {
                    name: 'fetch GB data',
                    value: async () => {
                        Logger.log('GB data', { origin: LogOrigin.CHEATS, data: [await cheats.fetchGBCData()] });
                    },
                },
                {
                    name: 'showState',
                    value: async () => {
                        cheats.showState();
                    },
                },
                {
                    name: 'marketing campaign',
                    value: async () => {
                        Logger.log(cheats.getMarketingCampaign(), LogOrigin.CHEATS);
                    },
                },
            ],
            reset: [
                {
                    name: 'clear player data',
                    value: () => cheats.clearPlayerData(),
                },
                {
                    name: 'Clear pre-boosters',
                    value: () => cheats.clearPreBoosters(),
                },
                {
                    name: 'reset friend data',
                    value: () => cheats.resetFriendData(),
                },
                {
                    name: 'reset FTUX',
                    value: () => cheats.resetFTUX(),
                },
                {
                    name: 'reset FB event',
                    value: () => cheats.resetFBEvent(),
                },
                {
                    name: 'reset fields',
                    value: () => cheats.resetFields(),
                },
                {
                    name: 'reset timers',
                    value: () => cheats.resetTimers(),
                },
            ],
            noParams: [
                {
                    name: 'star chest fall',
                    value: () => cheats.playStarChestFallAnimation(),
                },
                {
                    name: 'star chest FTUX',
                    value: () => cheats.playStarChestFTUX(),
                },
                {
                    name: 'clear last sunday purchase',
                    value: () => cheats.clearLastSundayPurchase(),
                },
                {
                    name: 'clear pet data',
                    value: () => cheats.clearPetData(),
                },
                {
                    name: 'enable purchases',
                    value: () => cheats.enablePurchases(),
                },
                {
                    name: 'debug XPromo',
                    value: () => cheats.debugXPromo(),
                },
                {
                    name: 'reset last claimed spin level',
                    value: () => cheats.resetLastClaimedSpinLevel(),
                },
            ],
            withParams: [
                {
                    name: 'set cheats global',
                    value: () => {
                        // @ts-ignore
                        window.cheats = cheats;
                    },
                },
                {
                    name: 'unlockBackcard',
                    value: () => {
                        this.mobileConsole.prompt('cheats.unlockBackcard(backcardId?: number)');
                    },
                },
                {
                    name: 'setQuestProgress',
                    value: () => {
                        this.mobileConsole.prompt('cheats.setQuestProgress(questNumber: number, progress: number)');
                    },
                },
                {
                    name: 'setBoosterReward',
                    value: () => {
                        Logger.log(JSON.stringify(BoosterType, null, 2), LogOrigin.CHEATS);
                        this.mobileConsole.prompt('cheats.setBoosterReward(boosterType: BoosterType, value: number)');
                    },
                },
                {
                    name: 'setBoosterCoins',
                    value: () => {
                        Logger.log(JSON.stringify(PlayerTask, null, 2), LogOrigin.CHEATS);
                        this.mobileConsole.prompt('cheats.setBoosterCoins(type: PlayerTask, value: number)');
                    },
                },
                {
                    name: 'showPopup',
                    value: () => {
                        Logger.log(JSON.stringify(PopupID, null, 2), LogOrigin.CHEATS);
                        this.mobileConsole.prompt('cheats.showPopup(popupName: PopupID, params: any)');
                    },
                },
                {
                    name: 'upgradeStarChest',
                    value: () => {
                        this.mobileConsole.prompt('cheats.upgradeStarChest(tier: number)');
                    },
                },
                {
                    name: 'playStarChestStarsRewardAnimation',
                    value: () => {
                        this.mobileConsole.prompt('cheats.playStarChestStarsRewardAnimation(amount: number, levelNumber?: number)');
                    },
                },
                {
                    name: 'setMissionProgress',
                    value: () => {
                        this.mobileConsole.prompt('cheats.setMissionProgress(questIndex: number, progress: number)');
                    },
                },
                {
                    name: 'setMissionType',
                    value: () => {
                        this.mobileConsole.prompt('cheats.setMissionType(questIndex: number, questType: QuestType, tier?: number)');
                    },
                },
                {
                    name: 'claimMissionReward',
                    value: () => {
                        this.mobileConsole.prompt('cheats.claimMissionReward(questIndex: number)');
                    },
                },
                {
                    name: 'completeMissionTimer',
                    value: () => {
                        this.mobileConsole.prompt('cheats.completeMissionTimer(questIndex?: number)');
                    },
                },
                {
                    name: 'setLoginStreak',
                    value: () => {
                        this.mobileConsole.prompt('cheats.setLoginStreak(value: number)');
                    },
                },
                {
                    name: 'setActivePet',
                    value: () => {
                        Logger.log(JSON.stringify(PetId, null, 2), LogOrigin.CHEATS);
                        this.mobileConsole.prompt('cheats.setActivePet(value: PetId, reload: boolean = true)');
                    },
                },
                {
                    name: 'movePet',
                    value: () => {
                        this.mobileConsole.prompt('cheats.movePet(nodesToMove: number, reload: boolean)');
                    },
                },
                {
                    name: 'startPetMiniGame',
                    value: () => {
                        this.mobileConsole.prompt('cheats.startPetMiniGame(props: { forceJackpotWin?: boolean })');
                    },
                },
                {
                    name: 'setMarketingCampaign',
                    value: () => {
                        this.mobileConsole.prompt('cheats.setMarketingCampaign(campaign: string)');
                    },
                },
                {
                    name: 'showFriendHelper',
                    value: () => {
                        this.mobileConsole.prompt('cheats.showFriendHelper(value: boolean = true)');
                    },
                },
            ],
        };

        Object.entries(foldersModel).forEach(([name, models]) => {
            const folder = this.gui.addFolder(name);
            // folder.open();

            models.forEach((item: any) => {
                Object.defineProperty(item, item.name, item);

                try {
                    const control = folder.add(item, item.name);
                    typeof item.step === 'number' && control.step(item.step);
                    typeof item.min === 'number' && control.min(item.min);
                    typeof item.max === 'number' && control.max(item.max);
                } catch (e) {
                    Logger.log(`Failed GUI control ${item.name}`, {
                        origin: LogOrigin.CHEATS,
                        data: [item],
                    }, LogStyles.ERROR);
                }
            });
        });
    }

    public toggle(): void {
        this.visible = !this.visible;
        this.visible ? this.gui.show() : this.gui.hide();

        if (this.visible) {
            window.localStorage?.setItem(CHEATS_GUI_ENABLED_STORAGE_KEY, '1');
        } else {
            window.localStorage?.removeItem(CHEATS_GUI_ENABLED_STORAGE_KEY);
        }
    }
}
